import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertTriangle, Package } from 'lucide-react';

import { JobMtl } from '../../services/types';

interface JobMtlTableProps {
  materials: JobMtl[];
}

const JobMtlTable: React.FC<JobMtlTableProps> = ({ materials }) => {
  const { t } = useTranslation();

  // Smart decimal formatting: show 2 decimals only if needed
  const formatQty = (value: number): string => {
    if (value % 1 === 0) {
      // No decimals - return as integer
      return value.toString();
    } else {
      // Has decimals - return with 2 decimal places
      return value.toFixed(2);
    }
  };

  if (materials.length === 0) {
    return (
      <div className="bg-white rounded border border-gray-300 shadow-sm">
        <div className="bg-blue-100 px-4 py-3 border-b border-blue-200">
          <h3 className="text-lg font-bold text-blue-900">
            {t('jobTracker.materials')} (0)
          </h3>
        </div>
        <div className="p-4 text-center text-gray-500">
          {t('jobTracker.noMaterials')}
        </div>
      </div>
    );
  }

  const totalMtls = materials.length;
  const issuedComplete = materials.filter((mtl) => mtl.issuedComplete).length;

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm flex flex-col">
      {/* Header */}
      <div className="bg-blue-100 border-l-4 border-l-blue-400 px-4 py-3 border-b border-blue-200 flex-shrink-0">
        <h3 className="text-lg font-bold text-blue-900">
          {t('jobTracker.materials')} ({totalMtls})
        </h3>
        <p className="text-xs text-blue-800 mt-1">
          {issuedComplete} {t('jobTracker.fullyIssued')}
        </p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto flex-grow">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 border-b border-gray-200 sticky top-0">
              <th className="px-4 py-3 text-left font-semibold text-gray-700 w-20">
                {t('jobTracker.mtlSeq')}
              </th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">
                {t('jobTracker.part', 'Part')}
              </th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">
                {t('jobTracker.description')}
              </th>
              <th className="px-4 py-3 text-center font-semibold text-gray-700 w-36">
                {t('jobTracker.statusColumn')}
              </th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700 w-28">
                {t('jobTracker.required')}
              </th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700 w-28">
                {t('jobTracker.issued')}
              </th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700 w-28">
                {t('jobTracker.available')}
              </th>
            </tr>
          </thead>
          <tbody>
            {materials.map((mtl) => {
              const issuedPct =
                mtl.requiredQty > 0
                  ? (mtl.issuedQty / mtl.requiredQty) * 100
                  : 0;
              const shortfall = mtl.requiredQty - mtl.issuedQty;
              const hasShortage = shortfall > 0 && mtl.availableQty < shortfall;

              return (
                <tr
                  key={`${mtl.jobNum}-${mtl.mtlSeq}`}
                  className="hover:bg-blue-50 transition-colors border-b border-gray-200"
                >
                  <td className="px-4 py-3 text-gray-900 font-medium">
                    {mtl.mtlSeq}
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/parts/${encodeURIComponent(mtl.partNum)}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                    >
                      {mtl.partNumUM}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {mtl.description}
                    {mtl.backFlush && (
                      <span className="ml-2 px-1.5 py-0.5 text-xs bg-purple-100 text-purple-800 rounded">
                        {t('jobTracker.backflush')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {mtl.issuedComplete ? (
                      <div className="flex justify-center">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                    ) : issuedPct > 0 && issuedPct < 100 ? (
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-1 flex-1 min-w-[100px]">
                          <div className="w-full bg-gray-200 rounded-full h-5 overflow-hidden">
                            <div
                              className="bg-blue-600 h-full flex items-center justify-center text-xs font-semibold text-white"
                              style={{ width: `${Math.min(issuedPct, 100)}%` }}
                            >
                              {issuedPct >= 25 && (
                                <span>{issuedPct.toFixed(0)}%</span>
                              )}
                            </div>
                          </div>
                          {issuedPct < 25 && (
                            <span className="text-xs text-blue-700 text-center">
                              {issuedPct.toFixed(0)}%
                            </span>
                          )}
                        </div>
                        {hasShortage && (
                          <div
                            title={t('jobTracker.inventoryShortage', {
                              available: formatQty(mtl.availableQty),
                              needed: formatQty(shortfall),
                            })}
                            className="flex-shrink-0"
                          >
                            <AlertTriangle className="w-4 h-4 text-red-600" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex justify-center">
                        <Package className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900">
                    {formatQty(mtl.requiredQty)} {mtl.iUM}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-medium">
                    {formatQty(mtl.issuedQty)} {mtl.iUM}
                  </td>
                  <td
                    className={`px-4 py-3 text-right font-medium ${
                      hasShortage ? 'text-red-700' : 'text-green-700'
                    }`}
                  >
                    {formatQty(mtl.availableQty)} {mtl.iUM}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobMtlTable;
